import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  startAuthentication,
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import backend from '../services/backend';
import { AUTH_SESSION_KEY, broadcastAuthSession, loginRedirect } from '../lib/authSession';
import { Field, FieldGroup, Fieldset, Label } from '../catalyst/fieldset';
import { Input } from '../catalyst/input';
import { Button } from '../catalyst/button';
import { Divider } from '../catalyst/divider';
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../catalyst/dialog';
import Logo from '../assets/brain.svg?react';
import { useEnvironmentFavicon } from '../hooks/useEnvironmentFavicon';
import { hasWebAuthn, isPasskeyCeremonyCancelled, passkeyOfferKey, shouldShowPasskeyButton } from './passkeys';

async function registerPasskey(): Promise<void> {
  const options = await backend.passkeyRegisterOptions();
  const credential = await startRegistration({
    optionsJSON: options.data as unknown as PublicKeyCredentialCreationOptionsJSON,
  });
  await backend.passkeyRegisterVerify(credential);
}

const passkeysAvailableQuery = {
  queryKey: ['passkeys', 'available'] as const,
  queryFn: () => backend.passkeysAvailable().then((res) => res.data),
};

export default function Login() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  useEnvironmentFavicon();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<'password' | 'passkey' | null>(null);
  const [offerUserId, setOfferUserId] = useState<string | null>(null);
  const [offerBusy, setOfferBusy] = useState(false);
  const [offerError, setOfferError] = useState<string | null>(null);

  const webAuthn = hasWebAuthn();
  const pendingRestore = useRef(false);

  const restoreSession = useCallback(() => {
    pendingRestore.current = false;
    queryClient.clear();
    navigate(loginRedirect(), { replace: true });
  }, [navigate, queryClient]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== AUTH_SESSION_KEY || event.newValue === null) return;
      if (offerUserId !== null || loading !== null) {
        pendingRestore.current = true;
        return;
      }
      restoreSession();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [offerUserId, loading, restoreSession]);

  useEffect(() => {
    if (offerUserId !== null || loading !== null) return;
    if (!pendingRestore.current) return;
    restoreSession();
  }, [offerUserId, loading, restoreSession]);

  const availableQuery = useQuery({
    ...passkeysAvailableQuery,
    enabled: webAuthn,
  });
  const showPasskeyButton = shouldShowPasskeyButton({
    hasWebAuthn: webAuthn,
    available: availableQuery.data?.enabled === true,
  });

  const resolvePasskeyAvailable = async (): Promise<boolean> => {
    if (!webAuthn) return false;
    if (availableQuery.isError) return false;
    if (availableQuery.isSuccess) return availableQuery.data.enabled === true;
    try {
      const data = await queryClient.ensureQueryData(passkeysAvailableQuery);
      return data.enabled === true;
    } catch {
      return false;
    }
  };

  const enterApp = restoreSession;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading('password');

    try {
      const response = await backend.login(username, password);
      broadcastAuthSession();
      const { id, hasPasskeys } = response.data;
      const available = await resolvePasskeyAvailable();
      if (
        !hasPasskeys &&
        !localStorage.getItem(passkeyOfferKey(id)) &&
        shouldShowPasskeyButton({ hasWebAuthn: webAuthn, available })
      ) {
        setOfferUserId(id);
        return;
      }
      enterApp();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(null);
    }
  };

  const handlePasskeySignIn = async () => {
    setError(null);
    setLoading('passkey');
    try {
      const options = await backend.passkeyAuthenticateOptions();
      const credential = await startAuthentication({
        optionsJSON: options.data as unknown as PublicKeyCredentialRequestOptionsJSON,
      });
      await backend.passkeyAuthenticateVerify(credential);
      broadcastAuthSession();
      enterApp();
    } catch (err) {
      setError(isPasskeyCeremonyCancelled(err) ? 'Sign-in cancelled' : 'Invalid credentials');
    } finally {
      setLoading(null);
    }
  };

  const closeOffer = () => {
    if (offerBusy) return;
    setOfferUserId(null);
    enterApp();
  };

  const skipOffer = () => {
    if (offerBusy) return;
    if (offerUserId) {
      localStorage.setItem(passkeyOfferKey(offerUserId), '1');
    }
    closeOffer();
  };

  const handleAddPasskey = async () => {
    setOfferError(null);
    setOfferBusy(true);
    try {
      await registerPasskey();
      enterApp();
    } catch (err) {
      setOfferError(isPasskeyCeremonyCancelled(err) ? 'Cancelled' : 'Could not add passkey');
    } finally {
      setOfferBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-sm space-y-6">
        <div className="flex flex-col items-center gap-3">
          <div className="flex size-12 items-center justify-center rounded-xl bg-zinc-900 text-white shadow-sm dark:bg-zinc-100 dark:text-zinc-900">
            <Logo className="size-7" />
          </div>
          <div className="text-center">
            <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
              Sign in to TRecs
            </h1>
            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
              Enter your credentials to continue
            </p>
          </div>
        </div>

        <div className="rounded-xl border border-zinc-950/10 bg-white p-6 shadow-sm sm:p-8 dark:border-zinc-700 dark:bg-zinc-800 dark:shadow-zinc-950/30">
          <div className="space-y-6">
            {showPasskeyButton && (
              <>
                <Button type="button" className="w-full" onClick={handlePasskeySignIn} disabled={loading !== null}>
                  {loading === 'passkey' ? 'Signing in...' : 'Sign in with passkey'}
                </Button>
                <Divider />
              </>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <Fieldset>
                <FieldGroup>
                  <Field>
                    <Label>Username</Label>
                    <Input
                      type="text"
                      name="username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      autoComplete="username"
                      autoFocus
                      required
                    />
                  </Field>
                  <Field>
                    <Label>Password</Label>
                    <Input
                      type="password"
                      name="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                    />
                  </Field>
                </FieldGroup>
              </Fieldset>

              {error && (
                <div className="rounded-lg bg-red-50 p-3 dark:bg-red-950/40">
                  <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={loading !== null}>
                {loading === 'password' ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>
          </div>
        </div>
      </div>

      <Dialog open={offerUserId !== null} onClose={closeOffer} size="sm">
        <DialogTitle>Add a passkey?</DialogTitle>
        <DialogDescription>
          Use Touch ID, Face ID, or Windows Hello the next time you sign in.
        </DialogDescription>
        {offerError && (
          <DialogBody>
            <p className="text-sm text-red-700 dark:text-red-400">{offerError}</p>
          </DialogBody>
        )}
        <DialogActions>
          <Button plain onClick={skipOffer} disabled={offerBusy}>
            Not now
          </Button>
          <Button color="blue" onClick={handleAddPasskey} disabled={offerBusy}>
            {offerBusy ? 'Adding…' : 'Add passkey'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
